import { atom } from 'jotai';
import { assignmentsService } from '../services/assignmentsService';
import { conversationService } from '@/services/conversationService';
import type {
  AssignmentResponse,
  AssignmentSummary,
  AssignmentsQueryParams,
  AssignmentStatus,
  AssignmentPriority,
  AssignmentUpdate,
  AssignmentStatusFilter,
} from '@/types';

import { EditorialAngle, Replica } from '@/types/assignments';
import type { DBConversationResponse, Draft } from '@/types/api';
import { draftService } from '@/services/draftService';
import { toast } from 'sonner';

// Core state atoms
export const assignmentsAtom = atom<AssignmentResponse[]>([]);
export const assignmentsSummaryAtom = atom<AssignmentSummary[]>([]);
export const assignmentsLoadingAtom = atom(false);
export const conversationsLoadingAtom = atom(false);
export const conversationsErrorAtom = atom<string | null>(null);
export const draftsLoadingAtom = atom(false);
export const draftsErrorAtom = atom<string | null>(null);
export const assignmentsErrorAtom = atom<string | null>(null);

export const assignmentConversationsAtom = atom<
  Record<string, DBConversationResponse[]>
>({});
export const assignmentDraftsAtom = atom<Record<string, Draft[]>>({});

// Filter atoms
export const assignmentsFilterAtom = atom<AssignmentStatusFilter>('all');
export const assignmentsPriorityFilterAtom = atom<'all' | AssignmentPriority>(
  'all'
);
export const assignmentsSearchQueryAtom = atom('');

// Replica state atoms
export const replicasAtom = atom<Replica[]>([]);
export const replicasLoadingAtom = atom(false);
export const replicasErrorAtom = atom<string | null>(null);

// Derived atom for filtered assignments
export const filteredAssignmentsAtom = atom(get => {
  const assignments = get(assignmentsAtom);
  const statusFilter = get(assignmentsFilterAtom);
  const priorityFilter = get(assignmentsPriorityFilterAtom);
  const searchQuery = get(assignmentsSearchQueryAtom);

  let filtered = assignments;

  // Apply status filter
  if (statusFilter !== 'all' && statusFilter !== 'overdue') {
    filtered = filtered.filter(a => a.status === statusFilter);
  }

  // Apply priority filter
  if (priorityFilter !== 'all') {
    filtered = filtered.filter(
      assignment => assignment.priority === priorityFilter
    );
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(
      a =>
        a.title.toLowerCase().includes(q) ||
        (a.description && a.description.toLowerCase().includes(q))
    );
  }

  return filtered;
});

// Async action to fetch assignments
export const fetchAssignmentsActionAtom = atom(
  null,
  async (get, set, params: AssignmentsQueryParams = {}) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const searchQuery = get(assignmentsSearchQueryAtom);
      const statusFilter = get(assignmentsFilterAtom);
      const priorityFilter = get(assignmentsPriorityFilterAtom);

      let assignments: AssignmentResponse[];

      // Use search if there's a query
      if (statusFilter === 'overdue') {
        // for overdue
        assignments = await assignmentsService.getOverdueAssignments({
          ...params,
          current_datetime: new Date().toISOString(),
        });
      } else if (searchQuery.trim()) {
        // search
        assignments = await assignmentsService.searchAssignments({
          q: searchQuery.trim(),
          ...params,
        });
      } else {
        // Build filter params
        const filterParams: AssignmentsQueryParams = { ...params };
        if (statusFilter !== 'all') filterParams.status = statusFilter;
        if (priorityFilter !== 'all') filterParams.priority = priorityFilter;

        assignments = await assignmentsService.getAssignments(filterParams);
      }

      set(assignmentsAtom, assignments);
      return assignments;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch assignments';
      set(assignmentsErrorAtom, errorMessage);
      console.error('Error fetching assignments:', error);

      // Return empty array to prevent further errors
      return [];
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Async action to fetch assignments summary
export const fetchAssignmentsSummaryActionAtom = atom(
  null,
  async (_get, set, params: { skip?: number; limit?: number } = {}) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const summary = await assignmentsService.getAssignmentsSummary(params);
      set(assignmentsSummaryAtom, summary);
      return summary;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch assignments summary';
      set(assignmentsErrorAtom, errorMessage);
      console.error('Error fetching assignments summary:', error);
      return [];
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to create an assignment
export const createAssignmentActionAtom = atom(
  null,
  async (get, set, assignmentData: any) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const newAssignment =
        await assignmentsService.createAssignment(assignmentData);

      // Add the new assignment to the current list
      const currentAssignments = get(assignmentsAtom);
      set(assignmentsAtom, [newAssignment, ...currentAssignments]);

      return newAssignment;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create assignment';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to update assignment status
export const updateAssignmentStatusActionAtom = atom(
  null,
  async (get, set, assignmentId: string, status: AssignmentStatus) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const updatedAssignment = await assignmentsService.updateAssignmentStatus(
        assignmentId,
        status
      );

      // Update the assignment in the current list
      const currentAssignments = get(assignmentsAtom);
      const updatedAssignments = currentAssignments.map(assignment =>
        assignment.id === assignmentId ? updatedAssignment : assignment
      );
      set(assignmentsAtom, updatedAssignments);

      return updatedAssignment;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update assignment status';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to generate editorial angle
export const generateEditorialAngleActionAtom = atom(
  null,
  async (
    _get,
    set,
    tipId: string,
    sourceId?: string,
    createAssignment: boolean = false
  ) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const params: any = { create_assignment: createAssignment };
      if (sourceId) {
        params.source_id = sourceId;
      }
      const result = await assignmentsService.generateEditorialAngle(
        tipId,
        params
      );

      // If an assignment was created, refresh the assignments list
      if (createAssignment && result.created_assignment) {
        // Refresh assignments list after creation
        try {
          const refreshedAssignments =
            await assignmentsService.getAssignments();
          set(assignmentsAtom, refreshedAssignments);
        } catch (refreshError) {
          console.error(
            'Could not refresh assignments after creation:',
            refreshError
          );
        }
      }
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate editorial angle';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to set Editorial Angle
export const setEditorialAngleActionAtom = atom(
  null,
  async (
    _get,
    set,
    assignmentId: string,
    editorialAngleMain: string | EditorialAngle
  ) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const editorialAngleString =
        typeof editorialAngleMain === 'string'
          ? editorialAngleMain
          : JSON.stringify(editorialAngleMain);

      const updatedAssignment: AssignmentResponse =
        await assignmentsService.setEditorialAngle(
          assignmentId,
          editorialAngleString
        );

      return updatedAssignment;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to set editorial angle';
      set(assignmentsErrorAtom, errorMessage);
      throw err;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to generate avatar briefing
export const generateAvatarBriefingActionAtom = atom(
  null,
  async (_get, set, assignmentId: string) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const result =
        await assignmentsService.generateAvatarBriefing(assignmentId);

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate avatar briefing';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to generate interview script
export const generateInterviewScriptActionAtom = atom(
  null,
  async (_get, set, assignmentId: string) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const result =
        await assignmentsService.generateInterviewScript(assignmentId);

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate interview script';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to fetch replicas
export const fetchReplicasActionAtom = atom(null, async (_get, set) => {
  try {
    set(replicasLoadingAtom, true);
    set(replicasErrorAtom, null);

    const replicas = await assignmentsService.getReplicas();
    set(replicasAtom, replicas);

    return replicas;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch replicas';
    set(replicasErrorAtom, errorMessage);

    toast.error(errorMessage);

    return [];
  } finally {
    set(replicasLoadingAtom, false);
  }
});

// Action to create Interviewer Persona
export const createInterviewerPersonaAtom = atom(
  null,
  async (_get, set, assignmentId: string, replicaId?: string) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const result = await assignmentsService.createPersona(
        assignmentId,
        replicaId
      );
      toast.success('Interviewer persona created successfully');

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create interviewer persona';
      set(assignmentsErrorAtom, errorMessage);

      toast.error(errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Action to delete an assignment
export const deleteAssignmentActionAtom = atom(
  null,
  async (get, set, assignmentId: string) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      await assignmentsService.deleteAssignment(assignmentId);

      // Remove the assignment from the current list
      const currentAssignments = get(assignmentsAtom);
      const updatedAssignments = currentAssignments.filter(
        assignment => assignment.id !== assignmentId
      );
      set(assignmentsAtom, updatedAssignments);

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete assignment';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Fetch single assignment action
export const fetchAssignmentActionAtom = atom(
  null,
  async (get, set, assignmentId: string) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const assignment = await assignmentsService.getAssignment(assignmentId);

      // Update the assignment in the current list if it exists
      const currentAssignments = get(assignmentsAtom);
      const existingIndex = currentAssignments.findIndex(
        a => a.id === assignmentId
      );
      if (existingIndex >= 0) {
        const updatedAssignments = [...currentAssignments];
        updatedAssignments[existingIndex] = assignment;
        set(assignmentsAtom, updatedAssignments);
      } else {
        // Add to the list if it doesn't exist
        set(assignmentsAtom, [...currentAssignments, assignment]);
      }

      return assignment;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch assignment';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Update assignment action
export const updateAssignmentActionAtom = atom(
  null,
  async (
    get,
    set,
    assignmentId: string,
    updateData: Partial<AssignmentUpdate>
  ) => {
    try {
      set(assignmentsLoadingAtom, true);
      set(assignmentsErrorAtom, null);

      const updatedAssignment = await assignmentsService.updateAssignment(
        assignmentId,
        updateData
      );

      // Update the assignment in the current list
      const currentAssignments = get(assignmentsAtom);
      const updatedAssignments = currentAssignments.map(assignment =>
        assignment.id === assignmentId ? updatedAssignment : assignment
      );
      set(assignmentsAtom, updatedAssignments);

      return updatedAssignment;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update assignment';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(assignmentsLoadingAtom, false);
    }
  }
);

// Clear assignments error action
export const clearAssignmentsErrorAtom = atom(null, (_get, set) => {
  set(assignmentsErrorAtom, null);
});

export const fetchAssignmentConversationsActionAtom = atom(
  null,
  async (_get, set, assignmentId: string) => {
    try {
      set(assignmentsErrorAtom, null);
      set(conversationsLoadingAtom, true);

      const conversations =
        await conversationService.getConversationsByAssignmentIdWithTranscripts(
          assignmentId
        );

      set(assignmentConversationsAtom, prev => ({
        ...prev,
        [assignmentId]: conversations,
      }));

      return conversations;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch assignment conversations';
      set(assignmentsErrorAtom, errorMessage);
      throw error;
    } finally {
      set(conversationsLoadingAtom, false);
    }
  }
);

export const fetchDraftsActionAtom = atom<null, [string], Promise<void>>(
  null,
  async (_, set, assignmentId: string) => {
    try {
      set(draftsErrorAtom, null);
      set(draftsLoadingAtom, true);

      const drafts = await draftService.getDrafts(assignmentId);

      set(assignmentDraftsAtom, prev => ({
        ...prev,
        [assignmentId]: drafts,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch assignment drafts';
      set(draftsErrorAtom, errorMessage);
      toast.error(errorMessage);
    } finally {
      set(draftsLoadingAtom, false);
    }
  }
);
export const createDraftActionAtom = atom(
  null,
  async (_, set, assignmentId: string) => {
    try {
      set(draftsErrorAtom, null);
      set(draftsLoadingAtom, true);

      await draftService.createDraft(assignmentId);
      const drafts = await draftService.getDrafts(assignmentId);

      set(assignmentDraftsAtom, prev => ({
        ...prev,
        [assignmentId]: drafts,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create assignment draft';
      set(draftsErrorAtom, errorMessage);
      toast.error(errorMessage);
    } finally {
      set(draftsLoadingAtom, false);
    }
  }
);

export const updateDraftActionAtom = atom(
  null,
  async (_, set, assignmentId: string, draftId: string, draftData: string) => {
    try {
      set(draftsErrorAtom, null);
      set(draftsLoadingAtom, true);

      const updatedDraft = await draftService.updateDraft(draftId, draftData);

      set(assignmentDraftsAtom, prev => {
        const currentDrafts = prev[assignmentId] ?? [];
        const drafts = currentDrafts.map(draft =>
          draft.id === draftId ? updatedDraft : draft
        );

        return {
          ...prev,
          [assignmentId]: drafts,
        };
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update assignment draft';
      toast.error(errorMessage);
    } finally {
      set(draftsLoadingAtom, false);
    }
  }
);
